/**
 * Krulichovi front-end behaviour.
 *
 * Header navigation (the mobile panel and submenu disclosures) and the sticky
 * header's scroll state. The CSS in assets/src/scss/layouts/_navmenu.scss
 * cannot open either nav affordance on its own — both toggles are real
 * <button> elements rather than a checkbox hack — so this file owns the whole
 * open/closed contract:
 *
 *   <body class="mobile-nav-active">   the mobile panel is open
 *   <li class="dropdown-active">       that item's submenu is open
 *   <body class="scrolled">            page has left the very top; drives the
 *                                      header/logo shrink in _header.scss
 *
 * All three class names are safelisted in gulpfile.js, because they only ever
 * exist at runtime and PurgeCSS cannot see them in any template.
 *
 * NOT implemented, though gulpfile.js safelists it: `preloader`. It has no CSS
 * yet, and a class toggled against no rule is worse than nothing — it reads as
 * working. Add the behaviour here when the styles exist.
 *
 * Concatenated after the vendor bundle by the gulp `scripts` task and enqueued
 * with `defer`, so the document is parsed before this runs. That is why there is
 * no DOMContentLoaded wrapper; every lookup below still guards for null so the
 * file is harmless if that ever changes.
 */

( function () {
	'use strict';

	/*
	 * Sticky header shrink. Unconditional (unlike the nav wiring below): it has
	 * no dependency on the nav markup existing, only on window/document, which
	 * are always present. Throttled to once per animation frame so the scroll
	 * handler never forces layout more than once per repaint, and read once on
	 * load too — a reload, back/forward navigation, or an anchor link can land
	 * the page already scrolled.
	 */
	var scrollTicking = false;

	function syncScrolled() {
		document.body.classList.toggle( 'scrolled', window.scrollY > 0 );
		scrollTicking = false;
	}

	window.addEventListener( 'scroll', function () {
		if ( scrollTicking ) {
			return;
		}

		scrollTicking = true;
		window.requestAnimationFrame( syncScrolled );
	}, { passive: true } );

	syncScrolled();

	// GLightbox is vendored (see gulpfile.js VENDOR_JS/VENDOR_CSS) but was
	// never initialised anywhere until the single-listing gallery needed it.
	// Generic over any `.glightbox` link site-wide, not Nemovitost-specific —
	// pure progressive enhancement, since every one of those links already
	// opens its full image directly with no JS at all.
	if ( window.GLightbox ) {
		window.GLightbox( { selector: '.glightbox' } );
	}

	/*
	 * Must match the desktop media query in _navmenu.scss, which is
	 * ($breakpoint-desktop + 1px) = 981px. The value is duplicated because CSS
	 * breakpoints are not readable from JS without a custom property round trip;
	 * if _variables.scss moves, move this too.
	 */
	var DESKTOP_QUERY = '(min-width: 981px)';

	var header = document.querySelector( '.header' );
	var navToggle = document.querySelector( '.mobile-nav-toggle' );
	var navList = document.getElementById( 'navmenu-list' );

	if ( ! header || ! navToggle || ! navList ) {
		return;
	}

	var desktop = window.matchMedia( DESKTOP_QUERY );

	/**
	 * Sets aria-expanded on a toggle button.
	 *
	 * @param {Element} button   The toggle.
	 * @param {boolean} expanded Whether its content is now open.
	 */
	function setExpanded( button, expanded ) {
		button.setAttribute( 'aria-expanded', expanded ? 'true' : 'false' );
	}

	/**
	 * Collapses every open submenu.
	 */
	function closeDropdowns() {
		var open = navList.querySelectorAll( '.dropdown-active' );

		Array.prototype.forEach.call( open, function ( item ) {
			item.classList.remove( 'dropdown-active' );

			var toggle = item.querySelector( '.toggle-dropdown' );

			if ( toggle ) {
				setExpanded( toggle, false );
			}
		} );
	}

	/**
	 * Closes the mobile panel, and any submenu inside it.
	 *
	 * @param {boolean} returnFocus Move focus back to the burger. Correct when
	 *                              the user dismissed the panel deliberately
	 *                              (Escape), wrong when they clicked elsewhere
	 *                              on the page and focus belongs where they put
	 *                              it.
	 */
	function closeNav( returnFocus ) {
		if ( ! document.body.classList.contains( 'mobile-nav-active' ) ) {
			return;
		}

		document.body.classList.remove( 'mobile-nav-active' );
		setExpanded( navToggle, false );
		closeDropdowns();

		if ( returnFocus ) {
			navToggle.focus();
		}
	}

	// The burger.
	navToggle.addEventListener( 'click', function () {
		var open = document.body.classList.toggle( 'mobile-nav-active' );

		setExpanded( navToggle, open );

		if ( ! open ) {
			closeDropdowns();
		}
	} );

	/*
	 * Submenu toggles, delegated: menu items are rendered by wp_nav_menu(), and
	 * delegation means an item added in wp-admin needs no rebind here.
	 */
	navList.addEventListener( 'click', function ( event ) {
		var toggle = event.target.closest( '.toggle-dropdown' );

		if ( ! toggle ) {
			return;
		}

		/*
		 * On desktop the submenu opens on hover and the toggle is display:none,
		 * so this only ever fires on mobile. Guarded anyway: a click can still
		 * be dispatched at a hidden element programmatically, and toggling a
		 * class the desktop CSS also reads would leave a submenu stuck open.
		 */
		if ( desktop.matches ) {
			return;
		}

		var item = toggle.closest( '.navmenu-item' );

		if ( ! item ) {
			return;
		}

		setExpanded( toggle, item.classList.toggle( 'dropdown-active' ) );
	} );

	/*
	 * A same-page anchor changes nothing about the document, so the panel would
	 * stay open over the content the link just scrolled to. Every other link
	 * unloads the page and closing is a no-op.
	 */
	navList.addEventListener( 'click', function ( event ) {
		if ( event.target.closest( '.toggle-dropdown' ) ) {
			return;
		}

		if ( event.target.closest( 'a' ) ) {
			closeNav( false );
		}
	} );

	// Escape dismisses the panel and hands focus back to the control that opened it.
	document.addEventListener( 'keydown', function ( event ) {
		if ( 'Escape' === event.key ) {
			closeNav( true );
		}
	} );

	// A click anywhere outside the header dismisses the panel.
	document.addEventListener( 'click', function ( event ) {
		if ( ! header.contains( event.target ) ) {
			closeNav( false );
		}
	} );

	/*
	 * Crossing into the desktop layout with the panel open leaves stale classes
	 * behind. `mobile-nav-active` is harmless there — the desktop rule for
	 * .navmenu-list comes later and wins — but `dropdown-active` is NOT: it is
	 * declared outside the media query and outranks the desktop submenu rule,
	 * so a submenu left open on mobile stays open on desktop. Reset both.
	 */
	desktop.addEventListener( 'change', function ( event ) {
		if ( event.matches ) {
			closeNav( false );
			closeDropdowns();
		}
	} );
}() );
